/**
 * Cast-error notice (#787) — the centre-screen "why didn't that cast?" channel.
 *
 * WoW-style placement: just above the character, in the lower-middle of the
 * play area. Deliberately NOT the action bar — the existing 150ms red slot
 * flash lives down there and its weakness is exactly that nobody is looking at
 * the bar mid-fight.
 *
 * NOT `RejectionToast`. That toast's own header says it "should never fire for
 * honest clients — if it does, it's a bug signal"; routing routine
 * out-of-resource play through it would destroy that diagnostic. This is the
 * ordinary-gameplay channel and they render independently (an out-of-resource
 * press must leave `rejection-toast` absent from the DOM).
 *
 * REASON-KEYED, not resource-specific: `copyFor` is a total function over
 * `CastBlockReason`, so out-of-range (#352), school-lock and invalid-target drop
 * in as extra arms with no structural change here. Only the resource arm ships.
 *
 * Copy/placement/duration are the three tunable constants below — see #787
 * ("tunable constants, not a design fork"). Tune in play.
 *
 * Lifetime: the notice owns its own dismissal timer and dispatches
 * `castError.drop` when it expires — the same shape `ServerErrorBanner` already
 * uses for its transient banner. NOT pruned on `hud.tick` like `rejections`/
 * `castFlashes`: that path only runs while the 10 Hz HUD sampler is alive, and
 * this cue must appear and vanish on its own schedule regardless. Coalescing
 * happens in the reducer (one entry per reason, `ts` refreshed), so a refresh
 * re-arms this timer instead of queueing a second message.
 */

import { useEffect, type ReactElement } from "react";
import { useStore } from "../store/context.js";
import type { CastBlock } from "../input/castability.js";

/** How long a notice stays on screen, ms. */
export const CAST_ERROR_TTL_MS = 1500;

/** Distance from the bottom of the play area, as a share of viewport height —
 *  above the action bar / unit frames, near the character. */
const BOTTOM_PCT = "38%";

/**
 * Player-facing copy for a block. Total over `CastBlockReason` (a new arm is a
 * compile error until it has copy). Never shows a raw enum: the sim's internal
 * `insufficientResource` never reaches the screen.
 */
export function copyFor(block: CastBlock): string {
  switch (block.reason) {
    case "insufficientResource":
      // `resourceId` IS the human word ("energy"/"mana"/"rage" — the content
      // `ResourceDef.id`), so no per-pool label table is needed. An unmapped /
      // absent pool degrades to the generic noun rather than printing "null".
      return block.resourceId === null
        ? "Not enough resource"
        : `Not enough ${block.resourceId}`;
  }
}

export function CastErrorNotice(): ReactElement | null {
  const { state, dispatch } = useStore();
  // Newest wins if several reasons are live at once (only one ships today).
  const latest = state.castErrors[state.castErrors.length - 1] ?? null;
  const reason = latest?.block.reason ?? null;
  const ts = latest?.ts ?? null;

  useEffect(() => {
    if (reason === null || ts === null) return;
    const remaining = CAST_ERROR_TTL_MS - (Date.now() - ts);
    const id = window.setTimeout(
      () => {
        dispatch({ type: "castError.drop", reason });
      },
      remaining > 0 ? remaining : 0,
    );
    return () => {
      window.clearTimeout(id);
    };
    // `ts` is in the dep list so a coalesced re-press (same reason, fresh ts)
    // re-arms the timer instead of dismissing on the FIRST press's schedule.
  }, [reason, ts, dispatch]);

  if (latest === null) return null;
  return (
    <div
      data-testid="cast-error"
      data-reason={latest.block.reason}
      style={{
        position: "absolute",
        bottom: BOTTOM_PCT,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 7,
        padding: "6px 14px",
        background: "rgba(20, 10, 10, 0.72)",
        border: "1px solid #7a2b2b",
        borderRadius: 6,
        color: "#ff6b6b",
        fontSize: 16,
        fontWeight: 600,
        textShadow: "0 1px 3px rgba(0,0,0,0.9)",
        pointerEvents: "none",
        whiteSpace: "nowrap",
      }}
    >
      {copyFor(latest.block)}
    </div>
  );
}
